import { Flex, Modal, ProgressCircle, Typography } from "@papercup-ai/orizuru";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import {
  SESSION_COUNTDOWN_REFRESH_MS,
  SESSION_WARNING_MS
} from "../../../constants/sessionTimeout";
import { useFeatureFlags } from "../../../featureFlags/useFeatureFlags";
import { sessionActivityAtom } from "../../../stateManagement/atoms/timeout.atom";

export const SessionTimeoutModal = () => {
  const { pathname } = useRouter();
  const [activity, setActivity] = useAtom(sessionActivityAtom);
  const { session_timeout_modal } = useFeatureFlags(["session_timeout_modal"]);
  const isStudio = pathname === "/studio" || pathname === "/studio-v2";
  const warning = activity.phase === "warning";
  const resuming = activity.phase === "resuming";
  let message = "Your editing segment is being made available to other users.";
  if (warning) {
    message =
      "Your editing segment will be unlocked when the timer ends unless you continue.";
  } else if (resuming) {
    message = "Checking your editing segment before you continue…";
  }

  const handleContinue = () => {
    if (
      warning &&
      activity.deadline !== null &&
      Date.now() >= activity.deadline
    ) {
      setActivity({ phase: "idle", deadline: null });
      return;
    }
    setActivity({ phase: warning ? "active" : "resuming", deadline: null });
  };

  return (
    <Modal
      title={warning ? "Are you still here?" : "You're inactive"}
      open={
        isStudio &&
        !!session_timeout_modal?.enabled &&
        activity.phase !== "active"
      }
      onConfirm={handleContinue}
      confirmButtonLabel={resuming ? "Resuming…" : "Continue working"}
      submitDisabled={resuming}
      cancelButtonHidden
      closeButton={false}
      closeOnEscape={false}
      closeOnInteractOutside={false}
      draggable={false}
      resizable={false}
      placement={"center"}
      hasOverlay
    >
      <Flex flexDirection={"column"} alignItems={"center"} gap={"20px"}>
        {warning && activity.deadline !== null && (
          <SessionCountdown deadline={activity.deadline} />
        )}
        <Typography variant={"labelMedium"} role={"status"}>
          {message}
        </Typography>
      </Flex>
    </Modal>
  );
};

const SessionCountdown = ({ deadline }: { deadline: number }) => {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const refresh = () => setNow(Date.now());
    refresh();
    const interval = setInterval(refresh, SESSION_COUNTDOWN_REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [deadline]);

  const remainingMs = Math.max(0, Math.min(SESSION_WARNING_MS, deadline - now));
  const seconds = Math.ceil(remainingMs / 1000);

  return (
    <ProgressCircle
      min={0}
      max={SESSION_WARNING_MS / 1000}
      value={remainingMs / 1000}
      size={"2xl"}
      aria-label={"Time remaining before inactivity"}
      aria-valuetext={`${seconds} seconds remaining`}
    >
      <Flex flexDirection={"column"} alignItems={"center"} gap={"4px"}>
        <Typography
          variant={"labelMedium"}
          fontSize={"40px"}
          lineHeight={"1"}
          fontVariantNumeric={"tabular-nums"}
        >
          {seconds}
        </Typography>
        <Typography variant={"labelSmall"}>{"seconds"}</Typography>
      </Flex>
    </ProgressCircle>
  );
};
